// @ts-nocheck
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Textarea } from "@libraryadmin/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@libraryadmin/components/ui/dialog";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@libraryadmin/components/ui/dropdown-menu";
import {
  Save,
  Printer,
  FileDown,
  Search,
  Edit,
  Trash2,
  Eye,
  Plus,
  AlertCircle,
  BookOpen,
  ArrowLeft,
  FileText,
  ExternalLink,
  BookCheck,
  Tag,
  Bookmark,
  Layers,
  FileSpreadsheet,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getBooks,
  createBook,
  updateBook,
  deleteBook,
  bulkImportBooks,
} from "@libraryadmin/services/api";
import type { Book } from "@libraryadmin/data/seed";
import type { BookImportResult } from "@libraryadmin/services/api";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";

const statusStyle: Record<string, string> = {
  available: "badge-success",
  issued: "badge-warning",
  reserved: "badge-info",
};

const BOOK_TABLE_COLUMNS = [
  { key: "identity", label: "Book No & ISBN" },
  { key: "title", label: "Title & Alias" },
  { key: "classification", label: "Type & Topic" },
  { key: "authorship", label: "Author / Tikakar / Translator" },
  { key: "language", label: "Language & Pages" },
  { key: "location", label: "Location" },
  { key: "pdf", label: "PDF" },
  { key: "status", label: "Status" },
] as const;

type BookTableColumn = (typeof BOOK_TABLE_COLUMNS)[number]["key"];

const DEFAULT_BOOK_COLUMNS = Object.fromEntries(
  BOOK_TABLE_COLUMNS.map(column => [column.key, true])
) as Record<BookTableColumn, boolean>;

const BOOK_COLUMNS_STORAGE_KEY = "library-admin-book-table-columns";

const bookTypes = [
  "Granth",
  "Agama",
  "Commentary (Tika)",
  "Philosophy",
  "Cosmology",
  "Biography",
  "Ethics",
  "General Reference",
  "Manuscript Study",
];

const bookTopics = [
  "Jain Philosophy",
  "Tattvartha Sutra",
  "Agama Literature",
  "Karma Theory",
  "Cosmology & Geography",
  "Monastic Ethics",
  "Prakrit Literature",
  "Sanskrit Commentaries",
  "History & Lineage",
  "Rituals & Prayers",
];

const MAX_MULTI_VALUES = 5;

function MultiValueBookFields({
  book,
  onChange,
}: {
  book: Omit<Book, "id"> | Book;
  onChange: (book: any) => void;
}) {
  const rowCount = Math.max(
    book.alternativeTitles?.length || 0,
    Math.max((book.authors?.length || 1) - 1, 0),
    Math.max((book.publishers?.length || 1) - 1, 0),
    Math.max((book.publicationYears?.length || 1) - 1, 0),
    Math.max((book.pageCounts?.length || 1) - 1, 0)
  );

  const updateRecord = (
    rowIndex: number,
    field: "title" | "language" | "author" | "publisher" | "year" | "pages",
    value: string
  ) => {
    const alternativeTitles = [...(book.alternativeTitles || [])];
    const authors = [...(book.authors || [{ name: book.author || "" }])];
    const publishers = [
      ...(book.publishers || [{ name: book.publisher || "" }]),
    ];
    const publicationYears = [
      ...(book.publicationYears || [{ year: Number(book.year) || 0 }]),
    ];
    const pageCounts = [
      ...(book.pageCounts || [{ pagesCount: Number(book.pages) || 0 }]),
    ];

    while (alternativeTitles.length <= rowIndex) {
      alternativeTitles.push({ title: "", language: "" });
    }
    while (authors.length <= rowIndex + 1) {
      authors.push({ name: "" });
    }
    while (publishers.length <= rowIndex + 1) {
      publishers.push({ name: "" });
    }
    while (publicationYears.length <= rowIndex + 1) {
      publicationYears.push({ year: 0 });
    }
    while (pageCounts.length <= rowIndex + 1) {
      pageCounts.push({ pagesCount: 0 });
    }

    if (field === "title" || field === "language") {
      alternativeTitles[rowIndex] = {
        ...alternativeTitles[rowIndex],
        [field]: value,
      };
    } else if (field === "author") {
      authors[rowIndex + 1] = { name: value };
    } else if (field === "publisher") {
      publishers[rowIndex + 1] = { name: value };
    } else if (field === "year") {
      publicationYears[rowIndex + 1] = { year: Number(value) || 0 };
    } else {
      pageCounts[rowIndex + 1] = { pagesCount: Number(value) || 0 };
    }

    onChange({
      ...book,
      alternativeTitles,
      authors,
      publishers,
      publicationYears,
      pageCounts,
    });
  };

  const addRecord = () => {
    if (rowCount >= MAX_MULTI_VALUES) {
      return;
    }
    const alternativeTitles = [...(book.alternativeTitles || [])];
    const authors = [...(book.authors || [{ name: book.author || "" }])];
    const publishers = [
      ...(book.publishers || [{ name: book.publisher || "" }]),
    ];
    const publicationYears = [
      ...(book.publicationYears || [{ year: Number(book.year) || 0 }]),
    ];
    const pageCounts = [
      ...(book.pageCounts || [{ pagesCount: Number(book.pages) || 0 }]),
    ];
    while (alternativeTitles.length < rowCount) {
      alternativeTitles.push({ title: "", language: "" });
    }
    while (authors.length < rowCount + 1) {
      authors.push({ name: "" });
    }
    while (publishers.length < rowCount + 1) {
      publishers.push({ name: "" });
    }
    while (publicationYears.length < rowCount + 1) {
      publicationYears.push({ year: 0 });
    }
    while (pageCounts.length < rowCount + 1) {
      pageCounts.push({ pagesCount: 0 });
    }

    onChange({
      ...book,
      alternativeTitles: [...alternativeTitles, { title: "", language: "" }],
      authors: [...authors, { name: "" }],
      publishers: [...publishers, { name: "" }],
      publicationYears: [...publicationYears, { year: 0 }],
      pageCounts: [...pageCounts, { pagesCount: 0 }],
    });
  };

  const removeRecord = (rowIndex: number) => {
    const alternativeTitles = [...(book.alternativeTitles || [])];
    const authors = [...(book.authors || [])];
    const publishers = [...(book.publishers || [])];
    const publicationYears = [...(book.publicationYears || [])];
    const pageCounts = [...(book.pageCounts || [])];
    alternativeTitles.splice(rowIndex, 1);
    authors.splice(rowIndex + 1, 1);
    publishers.splice(rowIndex + 1, 1);
    publicationYears.splice(rowIndex + 1, 1);
    pageCounts.splice(rowIndex + 1, 1);
    onChange({
      ...book,
      alternativeTitles,
      authors,
      publishers,
      publicationYears,
      pageCounts,
    });
  };

  return (
    <div className="space-y-4 rounded-xl border border-border bg-muted/20 p-4">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h4 className="text-sm font-bold text-foreground">
            Alternative Records
          </h4>
          <p className="text-xs text-muted-foreground">
            Add up to five alternate editions or language records.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={rowCount >= MAX_MULTI_VALUES}
          onClick={addRecord}
        >
          <Plus className="mr-1 h-4 w-4" /> Add Alternative Record
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-background">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="min-w-[220px]">Alternative Title</TableHead>
              <TableHead className="min-w-[130px]">Language</TableHead>
              <TableHead className="min-w-[180px]">Author</TableHead>
              <TableHead className="min-w-[180px]">Publisher</TableHead>
              <TableHead className="min-w-[110px]">Year</TableHead>
              <TableHead className="min-w-[110px]">Pages</TableHead>
              <TableHead className="w-16 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowCount === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No alternative records added.
                </TableCell>
              </TableRow>
            ) : (
              Array.from({ length: rowCount }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      value={book.alternativeTitles?.[index]?.title || ""}
                      placeholder="Alternative title"
                      onChange={event =>
                        updateRecord(index, "title", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={book.alternativeTitles?.[index]?.language || ""}
                      placeholder="Language"
                      onChange={event =>
                        updateRecord(index, "language", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={book.authors?.[index + 1]?.name || ""}
                      placeholder="Author"
                      onChange={event =>
                        updateRecord(index, "author", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={book.publishers?.[index + 1]?.name || ""}
                      placeholder="Publisher"
                      onChange={event =>
                        updateRecord(index, "publisher", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={book.publicationYears?.[index + 1]?.year || ""}
                      placeholder="Year"
                      onChange={event =>
                        updateRecord(index, "year", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="1"
                      value={book.pageCounts?.[index + 1]?.pagesCount || ""}
                      placeholder="Pages"
                      onChange={event =>
                        updateRecord(index, "pages", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove alternative record"
                      onClick={() => removeRecord(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function BookEntry() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Screen View Mode: "list" | "add" | "edit" | "view"
  const [viewMode, setViewMode] = useState<"list" | "add" | "edit" | "view">(
    "list"
  );

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("all");
  const [filterCondition, setFilterCondition] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [visibleColumns, setVisibleColumns] = useState<
    Record<BookTableColumn, boolean>
  >(() => {
    try {
      const stored = window.localStorage.getItem(BOOK_COLUMNS_STORAGE_KEY);
      return stored
        ? { ...DEFAULT_BOOK_COLUMNS, ...JSON.parse(stored) }
        : DEFAULT_BOOK_COLUMNS;
    } catch {
      return DEFAULT_BOOK_COLUMNS;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(
      BOOK_COLUMNS_STORAGE_KEY,
      JSON.stringify(visibleColumns)
    );
  }, [visibleColumns]);

  const visibleTableColumnCount =
    Object.values(visibleColumns).filter(Boolean).length + 1;

  // Active Selected Book for View/Edit/Delete
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [duplicateMode, setDuplicateMode] = useState<"skip" | "update">("skip");
  const [importResult, setImportResult] = useState<BookImportResult | null>(
    null
  );

  // New Book Initial Form State
  const initialForm: Omit<Book, "id"> = {
    bookNo: "",
    title: "",
    aliasName: "",
    publicationAddress: "",
    alternativeTitles: [],
    authors: [{ name: "" }],
    publishers: [{ name: "" }],
    publicationYears: [{ year: new Date().getFullYear() }],
    pageCounts: [{ pagesCount: 200 }],
    type: "Granth",
    topic: "Jain Philosophy",
    author: "",
    tikakar: "",
    translator: "",
    publisher: "",
    year: new Date().getFullYear().toString(),
    isbn: "",
    pages: 200,
    price: null,
    pdfLink: "",
    condition: "Good",
    rack: "1",
    bin: "A1",
    cupboard: "C1",
    language: "Gujarati",
    remarks: "",
    location: "",
    status: "available",
  };
  const [form, setForm] = useState(initialForm);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const booksQueryKey = ["books", debouncedSearch];
  const {
    data: books = [],
    isLoading,
    isFetching,
    isError,
    error: booksError,
  } = useQuery({
    queryKey: booksQueryKey,
    retry: false,
    queryFn: ({ signal }) =>
      getBooks(
        loadedBooks => {
          queryClient.setQueryData<Book[]>(booksQueryKey, loadedBooks);
        },
        debouncedSearch,
        signal
      ),
  });

  const showRequestError = (title: string, error: unknown) => {
    toast({
      title,
      description: error instanceof Error ? error.message : "Please try again.",
      variant: "destructive",
    });
  };

  const createMutation = useMutation({
    mutationFn: createBook,
    onSuccess: newBook => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Book Registered Successfully",
        description: `"${newBook.title}" (${newBook.bookNo}) added to Book Master.`,
      });
      setForm(initialForm);
      setViewMode("list");
    },
    onError: error => showRequestError("Unable to add book", error),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Book> }) =>
      updateBook(id, data),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast({
        title: "Book Master Updated",
        description: `"${updated.title}" record updated.`,
      });
      setSelectedBook(null);
      setViewMode("list");
    },
    onError: error => showRequestError("Unable to update book", error),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Book Deleted",
        description: "Book record removed from master catalog.",
        variant: "destructive",
      });
      setDeletingId(null);
    },
    onError: error => showRequestError("Unable to delete book", error),
  });

  const importMutation = useMutation({
    mutationFn: () => {
      if (!importFile) {
        throw new Error("Choose an Excel or CSV file.");
      }
      return bulkImportBooks(importFile, duplicateMode);
    },
    onSuccess: result => {
      setImportResult(result);
      queryClient.invalidateQueries({ queryKey: ["books"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Book import completed",
        description: `${result.created} created, ${result.updated} updated, ${result.skipped} skipped, ${result.failed} failed.`,
      });
    },
    onError: error => showRequestError("Unable to import books", error),
  });

  const handleImportFile = (file: File | null) => {
    if (!file) {
      setImportFile(null);
      return;
    }
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "xlsx" && extension !== "csv") {
      toast({
        title: "Unsupported file",
        description: "Choose an .xlsx or .csv file.",
        variant: "destructive",
      });
      return;
    }
    setImportFile(file);
    setImportResult(null);
  };

  // Filter Logic
  const filteredBooks = books.filter(b => {
    const matchesSearch = true;

    const matchesLang =
      filterLanguage === "all" ||
      b.language.toLowerCase() === filterLanguage.toLowerCase();
    const matchesCond =
      filterCondition === "all" ||
      b.condition.toLowerCase() === filterCondition.toLowerCase();
    const matchesStat =
      filterStatus === "all" ||
      b.status.toLowerCase() === filterStatus.toLowerCase();

    return matchesSearch && matchesLang && matchesCond && matchesStat;
  });

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filteredBooks);

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.bookNo || !form.title || !form.author) {
      toast({
        title: "Validation Error",
        description: "Book Number, Title, and Author are required.",
        variant: "destructive",
      });
      return;
    }
    createMutation.mutate({
      ...form,
      location:
        form.location || `Rack ${form.rack} / ${form.bin} / ${form.cupboard}`,
    });
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBook) {
      return;
    }
    if (!selectedBook.bookNo || !selectedBook.title || !selectedBook.author) {
      toast({
        title: "Validation Error",
        description: "Book Number, Title, and Author are required.",
        variant: "destructive",
      });
      return;
    }
    updateMutation.mutate({
      id: selectedBook.id,
      data: {
        ...selectedBook,
        location:
          selectedBook.location ||
          `Rack ${selectedBook.rack} / ${selectedBook.bin} / ${selectedBook.cupboard}`,
      },
    });
  };

  const startEdit = (book: Book) => {
    setSelectedBook(book);
    setViewMode("edit");
  };

  const startView = (book: Book) => {
    setSelectedBook(book);
    setViewMode("view");
  };

  const handleExportCSV = () => {
    let csvContent =
      "Book No,Title,Author,Publisher,Language,Location,Condition,Status\n";
    filteredBooks.forEach(b => {
      csvContent += `"${b.bookNo}","${b.title}","${b.author}","${b.publisher}","${b.language}","${b.location}","${b.condition}","${b.status}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "books_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DashboardLayout
      title="Book Master Directory"
      subtitle="Comprehensive library book master database, classification, metadata, and location indexing."
    >
      {/* ━━━ SCREEN 1: BOOK MASTER LISTING SCREEN ━━━ */}
      {viewMode === "list" && (
        <div className="animate-fade-in space-y-6">
          <div className="form-section">
            {/* Header Row */}
            <div className="mb-6 flex flex-col items-start justify-end gap-4 border-b pb-4 sm:flex-row sm:items-center">
              <div className="flex w-full flex-col gap-2.5 sm:ml-auto sm:w-auto sm:flex-row sm:items-center">
                <Button
                  onClick={() => {
                    setForm(initialForm);
                    setViewMode("add");
                  }}
                  className="btn-primary w-full justify-center sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4 shrink-0" /> Add Book to Master
                  (Screen Form)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-center rounded-lg sm:w-auto"
                  onClick={() => {
                    setImportFile(null);
                    setImportResult(null);
                    setDuplicateMode("skip");
                    setImportOpen(true);
                  }}
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Import Excel/CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center rounded-lg sm:w-auto"
                  onClick={handleExportCSV}
                >
                  <FileDown className="mr-2 h-4 w-4 shrink-0" /> Export CSV
                </Button>
              </div>
            </div>

            {/* Filters Bar */}
            <div className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search title, alias, author, ISBN..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="input-field pl-9 text-xs"
                />
              </div>

              <Select value={filterLanguage} onValueChange={setFilterLanguage}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {["English", "Hindi", "Gujarati", "Sanskrit", "Prakrit"].map(
                    l => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>

              <Select
                value={filterCondition}
                onValueChange={setFilterCondition}
              >
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  {["Excellent", "Good", "Fair", "Poor"].map(c => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="issued">Issued</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 justify-between gap-2 text-xs"
                  >
                    <span className="inline-flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4" /> Columns
                    </span>
                    <span className="text-muted-foreground">
                      {visibleTableColumnCount - 1}/{BOOK_TABLE_COLUMNS.length}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Show table columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {BOOK_TABLE_COLUMNS.map(column => (
                    <DropdownMenuCheckboxItem
                      key={column.key}
                      checked={visibleColumns[column.key]}
                      onCheckedChange={checked =>
                        setVisibleColumns(current => ({
                          ...current,
                          [column.key]: Boolean(checked),
                        }))
                      }
                      onSelect={event => event.preventDefault()}
                    >
                      {column.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() =>
                      setVisibleColumns({ ...DEFAULT_BOOK_COLUMNS })
                    }
                  >
                    Reset columns
                  </Button>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Master Catalog Table */}
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {visibleColumns.identity && (
                      <TableHead className="font-semibold">
                        Book No & ISBN
                      </TableHead>
                    )}
                    {visibleColumns.title && (
                      <TableHead className="font-semibold">
                        Title & Alias
                      </TableHead>
                    )}
                    {visibleColumns.classification && (
                      <TableHead className="font-semibold">
                        Type & Topic
                      </TableHead>
                    )}
                    {visibleColumns.authorship && (
                      <TableHead className="font-semibold">
                        Author / Tikakar / Translator
                      </TableHead>
                    )}
                    {visibleColumns.language && (
                      <TableHead className="font-semibold">
                        Lang / Pages
                      </TableHead>
                    )}
                    {visibleColumns.location && (
                      <TableHead className="font-semibold">Location</TableHead>
                    )}
                    {visibleColumns.pdf && (
                      <TableHead className="font-semibold">PDF</TableHead>
                    )}
                    {visibleColumns.status && (
                      <TableHead className="font-semibold">Status</TableHead>
                    )}
                    <TableHead className="text-right font-semibold">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && books.length === 0 ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: visibleTableColumnCount }).map(
                          (_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))
                  ) : isError && books.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleTableColumnCount}
                        className="py-12 text-center text-destructive"
                      >
                        {booksError instanceof Error
                          ? booksError.message
                          : "Unable to load book records."}
                      </TableCell>
                    </TableRow>
                  ) : filteredBooks.length === 0 && isFetching ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleTableColumnCount}
                        className="py-12 text-center text-muted-foreground"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          Loading book records…
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : filteredBooks.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleTableColumnCount}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No book records found matching criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {paginatedData.map(book => (
                        <TableRow
                          key={book.id}
                          className="table-row-hover text-xs"
                        >
                          {visibleColumns.identity && (
                            <TableCell>
                              <div className="font-mono font-bold text-foreground">
                                {book.bookNo || "Unknown"}
                              </div>
                              {book.isbn && (
                                <span className="font-mono text-[10px] text-muted-foreground">
                                  ISBN: {book.isbn}
                                </span>
                              )}
                            </TableCell>
                          )}

                          {visibleColumns.title && (
                            <TableCell>
                              <div>
                                <p className="text-sm font-bold leading-tight text-primary">
                                  {book.title || "Unknown"}
                                </p>
                                {book.aliasName && (
                                  <p className="mt-0.5 text-[11px] italic text-muted-foreground">
                                    Alias: "{book.aliasName}"
                                  </p>
                                )}
                              </div>
                            </TableCell>
                          )}

                          {visibleColumns.classification && (
                            <TableCell>
                              <div className="space-y-1">
                                <Badge
                                  variant="outline"
                                  className="border-primary/30 py-0 text-[10px] text-primary"
                                >
                                  {book.type || "Granth"}
                                </Badge>
                                {book.topic && (
                                  <p
                                    className="max-w-[120px] truncate text-[10px] text-muted-foreground"
                                    title={book.topic}
                                  >
                                    {book.topic}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                          )}

                          {visibleColumns.authorship && (
                            <TableCell>
                              <div className="space-y-0.5">
                                <p className="font-semibold text-foreground">
                                  {book.author || "Unknown"}
                                </p>
                                {book.tikakar && (
                                  <p className="text-[10px] text-muted-foreground">
                                    Tika: {book.tikakar}
                                  </p>
                                )}
                                {book.translator && (
                                  <p className="text-[10px] text-muted-foreground">
                                    Trans: {book.translator}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                          )}

                          {visibleColumns.language && (
                            <TableCell>
                              <p className="font-medium text-foreground">
                                {book.language || "Unknown"}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {book.pages ? `${book.pages} pages` : "Unknown"}
                              </p>
                            </TableCell>
                          )}

                          {visibleColumns.location && (
                            <TableCell className="font-mono text-[11px] text-muted-foreground">
                              {book.location || "Unknown"}
                            </TableCell>
                          )}

                          {visibleColumns.pdf && (
                            <TableCell>
                              {book.pdfLink ? (
                                <a
                                  href={book.pdfLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#8c3c15] hover:underline"
                                >
                                  <FileText className="h-3 w-3" /> PDF
                                </a>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">
                                  —
                                </span>
                              )}
                            </TableCell>
                          )}

                          {visibleColumns.status && (
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={statusStyle[book.status]}
                              >
                                {book.status}
                              </Badge>
                            </TableCell>
                          )}

                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startView(book)}
                                title="View Book Screen"
                              >
                                <Eye className="h-4 w-4 text-primary" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEdit(book)}
                                title="Edit Book Screen"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => setDeletingId(book.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {isFetching && (
                        <TableRow>
                          <TableCell
                            colSpan={visibleTableColumnCount}
                            className="py-3 text-center text-xs text-muted-foreground"
                          >
                            <span className="inline-flex items-center gap-2">
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                              Loading more books… {books.length} loaded
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* ━━━ SCREEN 2: ADD NEW BOOK FULL SCREEN FORM ━━━ */}
      {viewMode === "add" && (
        <div className="animate-fade-in space-y-6">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode("list")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Book Master Catalog
            </Button>
            <Badge
              variant="outline"
              className="border-primary/30 bg-primary/10 text-primary"
            >
              New Book Master Entry Screen
            </Badge>
          </div>

          <form
            onSubmit={handleCreateSubmit}
            className="form-section space-y-6"
          >
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-bold text-primary">
                  <BookOpen className="h-5 w-5 text-primary" /> Register New
                  Book in Master Directory
                </h2>
                <p className="text-xs text-muted-foreground">
                  Fill in primary metadata, alias name, classification types,
                  commentator/translator details, and PDF links.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setViewMode("list")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {createMutation.isPending ? "Saving..." : "Save Book Master"}
                </Button>
              </div>
            </div>

            {/* Section 1: Basic Information & Alias */}
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <Tag className="h-4 w-4 text-primary" /> 1. Primary Title &
                Alias Details
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Book No *</Label>
                  <Input
                    placeholder="e.g. A015"
                    className="input-field font-mono"
                    value={form.bookNo}
                    onChange={e => setForm({ ...form, bookNo: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <Label>Book Main Title *</Label>
                  <Input
                    placeholder="e.g. Tattvartha Sutra Commentary"
                    className="input-field font-semibold"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Alias Name (Alternative Name)</Label>
                  <Input
                    placeholder="e.g. Moksha Shastra"
                    className="input-field"
                    value={form.aliasName || ""}
                    onChange={e =>
                      setForm({ ...form, aliasName: e.target.value })
                    }
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Popular alias or alternate manuscript title
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Book Type</Label>
                  <Select
                    value={form.type}
                    onValueChange={v => setForm({ ...form, type: v })}
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {bookTypes.map(t => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Topic / Subject</Label>
                  <Select
                    value={form.topic}
                    onValueChange={v => setForm({ ...form, topic: v })}
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {bookTopics.map(tp => (
                        <SelectItem key={tp} value={tp}>
                          {tp}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 2: Authors, Tikakar & Translator */}
            <div className="space-y-4 pt-2">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <Layers className="h-4 w-4 text-primary" /> 2. Authorship,
                Tikakar & Translation Metadata
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Primary Author *</Label>
                  <Input
                    placeholder="e.g. Acharya Umasvati"
                    className="input-field"
                    value={form.author}
                    onChange={e =>
                      setForm({
                        ...form,
                        author: e.target.value,
                        authors: [
                          { name: e.target.value },
                          ...(form.authors || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Tikakar (Commentator)</Label>
                  <Input
                    placeholder="e.g. Pujyapada / Haribhadra Suri"
                    className="input-field"
                    value={form.tikakar || ""}
                    onChange={e =>
                      setForm({ ...form, tikakar: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Translator (Anuvadak)</Label>
                  <Input
                    placeholder="e.g. Pandit Sukhlalji"
                    className="input-field"
                    value={form.translator || ""}
                    onChange={e =>
                      setForm({ ...form, translator: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Publisher</Label>
                  <Input
                    placeholder="Publisher name"
                    className="input-field"
                    value={form.publisher}
                    onChange={e =>
                      setForm({
                        ...form,
                        publisher: e.target.value,
                        publishers: [
                          { name: e.target.value },
                          ...(form.publishers || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Publication Year</Label>
                  <Input
                    placeholder="e.g. 2018"
                    className="input-field"
                    value={form.year}
                    onChange={e =>
                      setForm({
                        ...form,
                        year: e.target.value,
                        publicationYears: [
                          { year: Number(e.target.value) || 0 },
                          ...(form.publicationYears || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>ISBN Number</Label>
                  <Input
                    placeholder="e.g. 978-3-16-148410-0"
                    className="input-field font-mono"
                    value={form.isbn || ""}
                    onChange={e => setForm({ ...form, isbn: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Publication Address</Label>
                <Input
                  placeholder="e.g. Ahmedabad, Gujarat, India"
                  className="input-field"
                  value={form.publicationAddress || ""}
                  onChange={e =>
                    setForm({ ...form, publicationAddress: e.target.value })
                  }
                />
              </div>

              <MultiValueBookFields book={form} onChange={setForm} />
            </div>

            {/* Section 3: Physical Specs, Language & Digital PDF Link */}
            <div className="space-y-4 pt-2">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <FileText className="h-4 w-4 text-primary" /> 3. Specifications,
                Language & Digital PDF Link
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div className="space-y-1.5">
                  <Label>Language</Label>
                  <Select
                    value={form.language}
                    onValueChange={v =>
                      setForm({ ...form, language: v as Book["language"] })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        "English",
                        "Hindi",
                        "Gujarati",
                        "Sanskrit",
                        "Prakrit",
                      ].map(l => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Pages Count</Label>
                  <Input
                    type="number"
                    placeholder="250"
                    className="input-field"
                    value={form.pages}
                    onChange={e =>
                      setForm({
                        ...form,
                        pages: Number(e.target.value),
                        pageCounts: [
                          { pagesCount: Number(e.target.value) || 0 },
                          ...(form.pageCounts || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Price</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="input-field"
                    value={form.price ?? ""}
                    onChange={e =>
                      setForm({
                        ...form,
                        price:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Condition</Label>
                  <Select
                    value={form.condition}
                    onValueChange={v =>
                      setForm({ ...form, condition: v as Book["condition"] })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Excellent", "Good", "Fair", "Poor"].map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Initial Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={v =>
                      setForm({ ...form, status: v as Book["status"] })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="issued">Issued</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5 text-[#8c3c15]" />{" "}
                  Digital PDF Link URL
                </Label>
                <Input
                  placeholder="https://library.org/digital-pdf/b101.pdf"
                  className="input-field font-mono text-xs"
                  value={form.pdfLink || ""}
                  onChange={e => setForm({ ...form, pdfLink: e.target.value })}
                />
                <p className="text-[10px] text-muted-foreground">
                  URL link to digitized PDF version of this book
                </p>
              </div>
            </div>

            {/* Section 4: Storage Location & Remarks */}
            <div className="space-y-4 pt-2">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <Bookmark className="h-4 w-4 text-primary" /> 4. Physical
                Storage Location & Remarks
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Location</Label>
                  <Input
                    placeholder="Library location"
                    className="input-field"
                    value={form.location}
                    onChange={e =>
                      setForm({ ...form, location: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Rack No</Label>
                  <Input
                    placeholder="Rack 1"
                    className="input-field"
                    value={form.rack}
                    onChange={e => setForm({ ...form, rack: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Bin No</Label>
                  <Input
                    placeholder="A1"
                    className="input-field"
                    value={form.bin}
                    onChange={e => setForm({ ...form, bin: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Cupboard No</Label>
                  <Input
                    placeholder="C1"
                    className="input-field"
                    value={form.cupboard}
                    onChange={e =>
                      setForm({ ...form, cupboard: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Remarks / Internal Notes</Label>
                <Textarea
                  placeholder="Donor reference, binding condition notes..."
                  className="input-field min-h-[90px]"
                  value={form.remarks || ""}
                  onChange={e => setForm({ ...form, remarks: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setViewMode("list")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                className="btn-primary"
              >
                <Save className="mr-2 h-4 w-4" />
                {createMutation.isPending
                  ? "Saving Book..."
                  : "Save Book Master Entry"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ━━━ SCREEN 3: EDIT BOOK FULL SCREEN FORM ━━━ */}
      {viewMode === "edit" && selectedBook && (
        <div className="animate-fade-in space-y-6">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode("list")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Book Master Catalog
            </Button>
            <Badge
              variant="outline"
              className="border-amber-500/30 bg-amber-500/10 text-amber-600"
            >
              Editing Book Master ({selectedBook.bookNo})
            </Badge>
          </div>

          <form
            onSubmit={handleUpdateSubmit}
            className="form-section space-y-6"
          >
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-bold text-primary">
                  <Edit className="h-5 w-5 text-primary" /> Edit Book Master
                  Details
                </h2>
                <p className="text-xs text-muted-foreground">
                  Updating record for "{selectedBook.title}".
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setViewMode("list")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>

            {/* Section 1: Basic Information & Alias */}
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <Tag className="h-4 w-4 text-primary" /> 1. Primary Title &
                Alias Details
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Book No</Label>
                  <Input
                    className="input-field font-mono"
                    placeholder="Unknown"
                    value={selectedBook.bookNo}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        bookNo: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <Label>Book Title</Label>
                  <Input
                    className="input-field font-semibold"
                    placeholder="Unknown"
                    value={selectedBook.title}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Alias Name</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.aliasName || ""}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        aliasName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Book Type</Label>
                  <Select
                    value={selectedBook.type || "__unknown"}
                    onValueChange={v =>
                      setSelectedBook({ ...selectedBook, type: v })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unknown" disabled>
                        Unknown
                      </SelectItem>
                      {selectedBook.type &&
                        !bookTypes.includes(selectedBook.type) && (
                          <SelectItem value={selectedBook.type}>
                            {selectedBook.type}
                          </SelectItem>
                        )}
                      {bookTypes.map(t => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Topic / Subject</Label>
                  <Select
                    value={selectedBook.topic || "__unknown"}
                    onValueChange={v =>
                      setSelectedBook({ ...selectedBook, topic: v })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unknown" disabled>
                        Unknown
                      </SelectItem>
                      {selectedBook.topic &&
                        !bookTopics.includes(selectedBook.topic) && (
                          <SelectItem value={selectedBook.topic}>
                            {selectedBook.topic}
                          </SelectItem>
                        )}
                      {bookTopics.map(tp => (
                        <SelectItem key={tp} value={tp}>
                          {tp}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 2: Author, Tikakar, Translator & Publication */}
            <div className="space-y-4 pt-2">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <Layers className="h-4 w-4 text-primary" /> 2. Authorship,
                Tikakar & Translator
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Author</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.author}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        author: e.target.value,
                        authors: [
                          { name: e.target.value },
                          ...(selectedBook.authors || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Tikakar (Commentator)</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.tikakar || ""}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        tikakar: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Translator (Anuvadak)</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.translator || ""}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        translator: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Publisher</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.publisher}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        publisher: e.target.value,
                        publishers: [
                          { name: e.target.value },
                          ...(selectedBook.publishers || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Publication Year</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.year}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        year: e.target.value,
                        publicationYears: [
                          { year: Number(e.target.value) || 0 },
                          ...(selectedBook.publicationYears || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>ISBN Number</Label>
                  <Input
                    className="input-field font-mono"
                    placeholder="Unknown"
                    value={selectedBook.isbn || ""}
                    onChange={e =>
                      setSelectedBook({ ...selectedBook, isbn: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Publication Address</Label>
                <Input
                  className="input-field"
                  placeholder="Unknown"
                  value={selectedBook.publicationAddress || ""}
                  onChange={e =>
                    setSelectedBook({
                      ...selectedBook,
                      publicationAddress: e.target.value,
                    })
                  }
                />
              </div>

              <MultiValueBookFields
                book={selectedBook}
                onChange={setSelectedBook}
              />
            </div>

            {/* Section 3: Specs, Status & Digital PDF */}
            <div className="space-y-4 pt-2">
              <h3 className="flex items-center gap-2 border-b pb-2 text-sm font-bold uppercase tracking-wider text-foreground">
                <FileText className="h-4 w-4 text-primary" /> 3. Specs, Digital
                PDF & Status
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div className="space-y-1.5">
                  <Label>Language</Label>
                  <Select
                    value={selectedBook.language || "__unknown"}
                    onValueChange={v =>
                      setSelectedBook({
                        ...selectedBook,
                        language: v as Book["language"],
                      })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unknown" disabled>
                        Unknown
                      </SelectItem>
                      {[
                        "English",
                        "Hindi",
                        "Gujarati",
                        "Sanskrit",
                        "Prakrit",
                      ].map(l => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Pages</Label>
                  <Input
                    type="number"
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.pages || ""}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        pages: Number(e.target.value),
                        pageCounts: [
                          { pagesCount: Number(e.target.value) || 0 },
                          ...(selectedBook.pageCounts || []).slice(1),
                        ],
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Price</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.price ?? ""}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        price:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Condition</Label>
                  <Select
                    value={selectedBook.condition || "__unknown"}
                    onValueChange={v =>
                      setSelectedBook({
                        ...selectedBook,
                        condition: v as Book["condition"],
                      })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unknown" disabled>
                        Unknown
                      </SelectItem>
                      {["Excellent", "Good", "Fair", "Poor"].map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select
                    value={selectedBook.status}
                    onValueChange={v =>
                      setSelectedBook({
                        ...selectedBook,
                        status: v as Book["status"],
                      })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="issued">Issued</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Digital PDF Link URL</Label>
                <Input
                  className="input-field font-mono text-xs"
                  placeholder="Unknown"
                  value={selectedBook.pdfLink || ""}
                  onChange={e =>
                    setSelectedBook({
                      ...selectedBook,
                      pdfLink: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Location</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.location || ""}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        location: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Rack</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.rack}
                    onChange={e =>
                      setSelectedBook({ ...selectedBook, rack: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Bin</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.bin}
                    onChange={e =>
                      setSelectedBook({ ...selectedBook, bin: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Cupboard</Label>
                  <Input
                    className="input-field"
                    placeholder="Unknown"
                    value={selectedBook.cupboard}
                    onChange={e =>
                      setSelectedBook({
                        ...selectedBook,
                        cupboard: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Remarks</Label>
                <Textarea
                  className="input-field min-h-[80px]"
                  placeholder="Unknown"
                  value={selectedBook.remarks || ""}
                  onChange={e =>
                    setSelectedBook({
                      ...selectedBook,
                      remarks: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setViewMode("list")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn-primary"
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending
                  ? "Updating..."
                  : "Update Book Master"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ━━━ SCREEN 4: VIEW FULL BOOK MASTER DETAILS SCREEN ━━━ */}
      {viewMode === "view" && selectedBook && (
        <div className="animate-fade-in space-y-6">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode("list")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Book Master Catalog
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEdit(selectedBook)}
              >
                <Edit className="mr-2 h-4 w-4" /> Edit Book Record
              </Button>
            </div>
          </div>

          <div className="form-section space-y-6">
            <div className="flex flex-col justify-between gap-4 border-b pb-4 sm:flex-row sm:items-center">
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded bg-primary px-2 py-0.5 font-mono text-sm font-bold text-primary-foreground">
                    {selectedBook.bookNo}
                  </span>
                  {selectedBook.isbn && (
                    <span className="font-mono text-xs text-muted-foreground">
                      ISBN: {selectedBook.isbn}
                    </span>
                  )}
                </div>
                <h2 className="mt-1 text-2xl font-bold text-primary">
                  {selectedBook.title}
                </h2>
                {selectedBook.aliasName && (
                  <p className="mt-0.5 text-sm italic text-muted-foreground">
                    Alias Name: "{selectedBook.aliasName}"
                  </p>
                )}
                {(selectedBook.alternativeTitles || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedBook.alternativeTitles?.map((item, index) => (
                      <Badge key={`${item.title}-${index}`} variant="secondary">
                        {item.title}
                        {item.language ? ` · ${item.language}` : ""}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={
                    statusStyle[selectedBook.status] + " px-3 py-1 text-xs"
                  }
                >
                  Status: {selectedBook.status}
                </Badge>
              </div>
            </div>

            {/* Metadata Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
              <div className="rounded-lg border bg-muted/40 p-3">
                <span className="block text-[10px] font-bold uppercase text-muted-foreground">
                  Primary Author
                </span>
                <span className="text-sm font-bold text-foreground">
                  {(selectedBook.authors || [])
                    .map(item => item.name)
                    .filter(Boolean)
                    .join(", ") ||
                    selectedBook.author ||
                    "Unknown"}
                </span>
              </div>

              <div className="rounded-lg border bg-muted/40 p-3">
                <span className="block text-[10px] font-bold uppercase text-muted-foreground">
                  Tikakar (Commentator)
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {selectedBook.tikakar || "Unknown"}
                </span>
              </div>

              <div className="rounded-lg border bg-muted/40 p-3">
                <span className="block text-[10px] font-bold uppercase text-muted-foreground">
                  Translator (Anuvadak)
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {selectedBook.translator || "Unknown"}
                </span>
              </div>

              <div className="rounded-lg border bg-muted/40 p-3">
                <span className="block text-[10px] font-bold uppercase text-muted-foreground">
                  Publisher
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {(selectedBook.publishers || [])
                    .map(item => item.name)
                    .filter(Boolean)
                    .join(", ") ||
                    selectedBook.publisher ||
                    "Unknown"}
                  {" · "}
                  {(selectedBook.publicationYears || [])
                    .map(item => item.year)
                    .filter(Boolean)
                    .join(", ") ||
                    selectedBook.year ||
                    "Unknown"}
                </span>
              </div>
            </div>

            {/* Classification & Location Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2 rounded-lg border bg-card p-4">
                <span className="block border-b pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Classification
                </span>
                <div className="space-y-1 text-xs">
                  <p>
                    <span className="text-muted-foreground">Type:</span>{" "}
                    <strong>{selectedBook.type || "Granth"}</strong>
                  </p>
                  <p>
                    <span className="text-muted-foreground">Topic:</span>{" "}
                    <strong>{selectedBook.topic || "Jain Philosophy"}</strong>
                  </p>
                  <p>
                    <span className="text-muted-foreground">Language:</span>{" "}
                    <strong>{selectedBook.language || "Unknown"}</strong>
                  </p>
                  <p>
                    <span className="text-muted-foreground">Pages:</span>{" "}
                    <strong>
                      {(selectedBook.pageCounts || [])
                        .map(item => item.pagesCount)
                        .filter(Boolean)
                        .join(", ") ||
                        selectedBook.pages ||
                        "Unknown"}
                      {selectedBook.pages ? " pages" : ""}
                    </strong>
                  </p>
                </div>
              </div>

              <div className="space-y-2 rounded-lg border bg-card p-4">
                <span className="block border-b pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Physical Storage Location
                </span>
                <div className="space-y-1 font-mono text-xs">
                  <p>
                    <span className="text-muted-foreground">Rack:</span>{" "}
                    {selectedBook.rack || "Unknown"}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Bin:</span>{" "}
                    {selectedBook.bin || "Unknown"}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Cupboard:</span>{" "}
                    {selectedBook.cupboard || "Unknown"}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Full Tag:</span>{" "}
                    <strong className="text-primary">
                      {selectedBook.location || "Unknown"}
                    </strong>
                  </p>
                  <p>
                    <span className="text-muted-foreground">
                      Publication Address:
                    </span>{" "}
                    <strong>
                      {selectedBook.publicationAddress || "Unknown"}
                    </strong>
                  </p>
                </div>
              </div>

              <div className="space-y-2 rounded-lg border bg-card p-4">
                <span className="block border-b pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Digital Media & Access
                </span>
                <div className="space-y-2 text-xs">
                  {selectedBook.pdfLink ? (
                    <div>
                      <span className="mb-1 block text-muted-foreground">
                        Digitized PDF Version:
                      </span>
                      <a
                        href={selectedBook.pdfLink}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"
                      >
                        <FileText className="h-4 w-4" /> Open Digital PDF
                      </a>
                    </div>
                  ) : (
                    <p className="italic text-muted-foreground">
                      No digital PDF file attached.
                    </p>
                  )}
                  <p>
                    <span className="text-muted-foreground">Condition:</span>{" "}
                    <Badge variant="outline">
                      {selectedBook.condition || "Unknown"}
                    </Badge>
                  </p>
                </div>
              </div>
            </div>

            {selectedBook.remarks && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-300">
                <strong className="mb-0.5 block">Remarks / Donor Notes:</strong>
                {selectedBook.remarks}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" /> Import Books
            </DialogTitle>
            <DialogDescription>
              Upload the original .xlsx or .csv file. All 11 required column
              headers must be present in row 1.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="books-import-file">Excel or CSV file *</Label>
                <Input
                  id="books-import-file"
                  type="file"
                  accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  onChange={e => handleImportFile(e.target.files?.[0] || null)}
                />
                {importFile && (
                  <p className="text-xs text-muted-foreground">
                    {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>When book number already exists</Label>
                <Select
                  value={duplicateMode}
                  onValueChange={value =>
                    setDuplicateMode(value as "skip" | "update")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skip">Skip existing books</SelectItem>
                    <SelectItem value="update">
                      Update existing books
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
              Required headers: Books Num., Book&apos;s Name, Location,
              Language, Year, Price, Page, Author, Editor/Translator, Publisher,
              and Topic/Subject.
            </div>

            <div className="overflow-hidden rounded-lg border">
              <div className="border-b bg-muted/40 px-4 py-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Excel column mapping
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Row 1 headers must match the Excel column names exactly.
                </p>
              </div>
              <div className="max-h-64 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Excel column</TableHead>
                      <TableHead>Book field</TableHead>
                      <TableHead>Requirement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      ["Books Num.", "Book Number", "Required"],
                      ["Book's Name", "Book Main Title", "Required"],
                      ["Location", "Location", "Optional"],
                      ["Language", "Language", "Optional"],
                      ["Year", "Publication Year", "Number"],
                      ["Price", "Price", "Number"],
                      ["Page", "Pages Count", "Number"],
                      ["Author", "Primary Author", "Required"],
                      ["Editor/Translator", "Translator", "Optional"],
                      ["Publisher", "Publisher", "Optional"],
                      ["Topic/Subject", "Topic / Subject", "Optional"],
                    ].map(([excelColumn, bookField, requirement]) => (
                      <TableRow key={excelColumn}>
                        <TableCell className="font-medium">
                          {excelColumn}
                        </TableCell>
                        <TableCell>{bookField}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              requirement === "Required" ? "default" : "outline"
                            }
                          >
                            {requirement}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {importResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {[
                    ["Total", importResult.total_rows],
                    ["Created", importResult.created],
                    ["Updated", importResult.updated],
                    ["Skipped", importResult.skipped],
                    ["Failed", importResult.failed],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-lg border bg-card p-3 text-center"
                    >
                      <p className="text-xl font-bold text-foreground">
                        {value}
                      </p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>

                {importResult.errors?.length > 0 && (
                  <div className="overflow-hidden rounded-lg border">
                    <div className="border-b bg-destructive/5 px-4 py-2 text-sm font-semibold text-destructive">
                      Row errors
                    </div>
                    <div className="max-h-64 overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Row</TableHead>
                            <TableHead>Field</TableHead>
                            <TableHead>Message</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importResult.errors.map((item, index) => (
                            <TableRow
                              key={`${item.row}-${item.field}-${index}`}
                            >
                              <TableCell>{item.row}</TableCell>
                              <TableCell className="font-mono text-xs">
                                {item.field}
                              </TableCell>
                              <TableCell>{item.message}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>
              Close
            </Button>
            <Button
              className="btn-primary"
              disabled={!importFile || importMutation.isPending}
              onClick={() => importMutation.mutate()}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {importMutation.isPending ? "Importing..." : "Import Books"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingId}
        onOpenChange={open => !open && setDeletingId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Delete Book Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this book from the master
              directory?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
